export type GuideSlug =
  | 'evidencija-radnog-vremena'
  | 'vise-lokacija'
  | 'prisustvo-i-pristup'
  | 'priprema-za-kontrolu'
  | 'workforce-admin-playbook';

export type GuidePage = {
  slug: GuideSlug;
  metadata: { title: string; description: string };
  eyebrow: string;
  title: string;
  subtitle: string;
  sections: { title: string; body: string; bullets?: string[] }[];
  cta: { title: string; body: string; button: string };
};

type GuidesCopy = Record<GuideSlug, GuidePage>;

const srGuides: GuidesCopy = {
  'evidencija-radnog-vremena': {
    slug: 'evidencija-radnog-vremena',
    metadata: {
      title: 'Šta ozbiljna firma traži od sistema evidencije | Cadrino ERV',
      description:
        'Kriterijumi za izbor sistema prisustva i pristupa bez nagađanja i ručnog spajanja podataka.',
    },
    eyebrow: 'Vodič',
    title: 'Šta ozbiljna firma treba od sistema evidencije',
    subtitle:
      'Dobar sistem nije samo lista čekiranja. To je operativni pregled koji HR, operativa i rukovodstvo mogu da koriste svakog dana.',
    sections: [
      {
        title: 'Jedan odgovor na ključna pitanja',
        body: 'Pre izbora sistema, postavite pitanja koja otkrivaju da li će tim zaista imati kontrolu:',
        bullets: [
          'ko je prisutan i ko sme gde',
          'da li uređaji rade i šta čeka obradu',
          'da li izveštaj odgovara onome što je zabeleženo',
          'da li sistem radi bez stalnog pozivanja podrške',
        ],
      },
      {
        title: 'Manje Excela, više reda',
        body:
          'Cadrino ERV povezuje uređaje, prisustvo i pristup u jedan pregled. Cilj nije više funkcija na papiru, već jasnija slika u radu.',
      },
    ],
    cta: {
      title: 'Hoćete da vidite kako to izgleda kod vas?',
      body: 'Zakažite demo: prolazimo vaše lokacije, uređaje i pravila pristupa.',
      button: 'Zakažite demo',
    },
  },
  'vise-lokacija': {
    slug: 'vise-lokacija',
    metadata: {
      title: 'Više lokacija, jedan pregled, Cadrino ERV',
      description:
        'Kako firme sa više objekata drže prisustvo, pristup i uređaje pod kontrolom.',
    },
    eyebrow: 'Vodič',
    title: 'Više lokacija. Jedan pregled.',
    subtitle:
      'Kada firma raste na više objekata, problem nije samo broj uređaja, problem je da niko nema istu sliku.',
    sections: [
      {
        title: 'Ista pitanja na svakoj lokaciji',
        body: 'Operativni tim mora da zna:',
        bullets: [
          'da li uređaji rade',
          'ko ima pristup gde',
          'šta čeka obradu pre nego što krene obračun',
        ],
      },
      {
        title: 'Jedan sistem, jasna granica odgovornosti',
        body:
          'Cadrino ERV daje centralni pregled uz pravila po lokaciji i uređaju, bez ručnog spajanja izveštaja iz svakog objekta.',
      },
    ],
    cta: {
      title: 'Imate više lokacija?',
      body: 'Na demo razgovoru mapiramo vašu strukturu i pokazujemo pregled koji tim zaista koristi.',
      button: 'Zakažite demo',
    },
  },
  'prisustvo-i-pristup': {
    slug: 'prisustvo-i-pristup',
    metadata: {
      title: 'Prisustvo i pristup u jednom sistemu, Cadrino ERV',
      description:
        'Zašto firme spajaju evidenciju dolazaka i kontrolu pristupa u jedan operativni pregled.',
    },
    eyebrow: 'Vodič',
    title: 'Prisustvo i pristup, jedna operativna istina',
    subtitle:
      'Kada prisustvo i pristup žive u odvojenim tabelama, greške se vide tek kad zabole. Jedan pregled menja svakodnevni rad.',
    sections: [
      {
        title: 'Dva procesa, jedan tim',
        body:
          'HR gleda sate. Operativa gleda ko sme u objekat. Rukovodstvo pita da li su podaci tačni. Sistem mora da odgovori svima istovremeno.',
      },
      {
        title: 'Kategorija koju Cadrino ERV gradi',
        body:
          'Operativna kontrola dolazaka, pristupa i uređaja, ne još jedna generička evidencija, već jasan standard rada.',
      },
    ],
    cta: {
      title: 'Vidite sistem u akciji',
      body: 'Demo na vašem scenariju: uređaji, pravila, pregled.',
      button: 'Zakažite demo',
    },
  },
  'priprema-za-kontrolu': {
    slug: 'priprema-za-kontrolu',
    metadata: {
      title: 'Digitalna evidencija i pregled podataka, Cadrino ERV',
      description:
        'Kako firme pripremaju jasan pregled evidencije. Informativno, ne pravni savet.',
    },
    eyebrow: 'Vodič',
    title: 'Jasan pregled pre nego što zatreba',
    subtitle:
      'Kontrole i interne provere traže dokaz da sistem radi predvidivo. Poenta nije panika na kraju meseca, već red tokom meseca.',
    sections: [
      {
        title: 'Informativno, ne pravni savet',
        body:
          'Ovaj tekst ne zamenjuje pravno savetovanje. Pokazuje kako digitalni pregled pomaže timu da ima strukturisane podatke kada ih zatraže.',
      },
      {
        title: 'Šta firme obično traže',
        body: 'U praksi pomaže kada sistem omogućava:',
        bullets: [
          'izvoz po periodu i lokaciji',
          'trag ko je menjao pristup ili podatke',
          'vidljivost šta je obrađeno i šta čeka',
        ],
      },
    ],
    cta: {
      title: 'Proverite da li vaš proces drži korak',
      body: 'Na demo pokazujemo izvoz, pregled i rad sa uređajima na vašem primeru.',
      button: 'Zakažite demo',
    },
  },
  'workforce-admin-playbook': {
    slug: 'workforce-admin-playbook',
    metadata: {
      title: 'Playbook za workforce administraciju | Cadrino ERV',
      description:
        'Operativni vodič za politike, odobrenja pregleda, compliance overrajde i izveštaje.',
    },
    eyebrow: 'Playbook',
    title: 'Playbook za administraciju radnog vremena',
    subtitle:
      'Kratak vodič za admine: workflow pregleda, SLA, compliance overrajdi i reporting paket.',
    sections: [
      {
        title: 'Dnevna kontrolna lista',
        body: 'Pre obračuna ili zatvaranja nedelje:',
        bullets: [
          'isprazniti otvorene stavke u redu pregleda evidencije',
          'proveriti starost reda pregleda za SLA prekoračenja',
          'uzorak compliance overrajda u shift audit logu',
        ],
      },
      {
        title: 'Puna dokumentacija',
        body:
          'Detaljni playbook po ulogama: ADMIN_PLAYBOOK.md, SUPERVISOR_PLAYBOOK.md i HR_PLAYBOOK.md u docs folderu repozitorijuma.',
      },
    ],
    cta: {
      title: 'Pomoć pri uvođenju?',
      body: 'Zakažite demo prolaz kroz workflow pregleda i izveštaje za vaš tenant.',
      button: 'Zakažite demo',
    },
  },
};

const enGuides: GuidesCopy = {
  'evidencija-radnog-vremena': {
    ...srGuides['evidencija-radnog-vremena'],
    metadata: {
      title: 'What a serious company needs from attendance software, Cadrino ERV',
      description:
        'Criteria for choosing attendance and access visibility without guesswork.',
    },
    title: 'What a serious company needs from attendance software',
    subtitle:
      'A good system is not just a list of clock-ins. It is an operational view HR, operations, and leadership can use every day.',
    sections: [
      {
        title: 'One answer to key questions',
        body: 'Before choosing a system, ask:',
        bullets: [
          'who is present and who may go where',
          'whether devices work and what is waiting to process',
          'whether reports match what was recorded',
          'whether the team can work without constant support calls',
        ],
      },
      {
        title: 'Less spreadsheet chaos',
        body:
          'Cadrino ERV connects devices, attendance, and access in one view. The goal is clarity in daily work, not more features on paper.',
      },
    ],
    cta: {
      title: 'Want to see how this works for you?',
      body: 'Book a demo, we walk through your locations, devices, and access rules.',
      button: 'Book a demo',
    },
  },
  'vise-lokacija': {
    ...srGuides['vise-lokacija'],
    metadata: {
      title: 'Multiple locations, one view, Cadrino ERV',
      description: 'How multi-site companies keep attendance, access, and devices under control.',
    },
    title: 'Multiple locations. One view.',
    subtitle:
      'As you add sites, the challenge is not device count, it is that nobody shares the same picture.',
    sections: [
      {
        title: 'The same questions at every site',
        body: 'Operations needs to know:',
        bullets: [
          'whether devices are online',
          'who has access where',
          'what is waiting before payroll',
        ],
      },
      {
        title: 'One system, clear ownership',
        body:
          'Cadrino ERV provides a central view with rules per location and device, without manual merges from each site.',
      },
    ],
    cta: {
      title: 'Running multiple locations?',
      body: 'On a demo we map your structure and show the view your team will actually use.',
      button: 'Book a demo',
    },
  },
  'prisustvo-i-pristup': {
    ...srGuides['prisustvo-i-pristup'],
    metadata: {
      title: 'Attendance and access in one system, Cadrino ERV',
      description: 'Why companies unify clock-ins and access control in one operational view.',
    },
    title: 'Attendance and access, one operational truth',
    subtitle:
      'When attendance and access live in separate tables, problems surface too late. One view changes daily work.',
    sections: [
      {
        title: 'Two processes, one team',
        body:
          'HR watches hours. Operations watches who may enter. Leadership asks if data is accurate. The system must answer all three.',
      },
      {
        title: 'The category Cadrino ERV builds',
        body:
          'Operational control of arrivals, access, and devices, a clear standard, not generic record-keeping.',
      },
    ],
    cta: {
      title: 'See the system in action',
      body: 'A demo on your scenario: devices, rules, visibility.',
      button: 'Book a demo',
    },
  },
  'priprema-za-kontrolu': {
    ...srGuides['priprema-za-kontrolu'],
    metadata: {
      title: 'Digital records and clear visibility, Cadrino ERV',
      description: 'How companies prepare structured attendance data. Informational, not legal advice.',
    },
    title: 'Clear visibility before you need it',
    subtitle:
      'Reviews and internal checks need proof the system works predictably. The point is order during the month, not panic at month-end.',
    sections: [
      {
        title: 'Informational, not legal advice',
        body:
          'This page does not replace legal counsel. It shows how digital visibility helps teams hold structured data when asked.',
      },
      {
        title: 'What companies typically need',
        body: 'In practice it helps when the system enables:',
        bullets: [
          'export by period and location',
          'trace of access and data changes',
          'visibility into processed vs waiting items',
        ],
      },
    ],
    cta: {
      title: 'Check whether your process keeps pace',
      body: 'On a demo we show export, visibility, and devices on your example.',
      button: 'Book a demo',
    },
  },
  'workforce-admin-playbook': {
    ...srGuides['workforce-admin-playbook'],
    metadata: {
      title: 'Workforce admin playbook | Cadrino ERV',
      description:
        'Operational checklist for policies, review approvals, compliance overrides, and workforce reports.',
    },
    title: 'Workforce administration playbook',
    subtitle:
      'Short operational guide for tenant admins: review workflow, SLA, compliance overrides, and reporting pack.',
    sections: [
      {
        title: 'Daily checklist',
        body: 'Before payroll or week close:',
        bullets: [
          'clear open attendance review queue items',
          'check review queue aging for SLA breaches',
          'sample compliance overrides in shift audit log',
        ],
      },
      {
        title: 'Full documentation',
        body:
          'Detailed role-based playbooks live in the repository docs folder: ADMIN_PLAYBOOK.md, SUPERVISOR_PLAYBOOK.md, and HR_PLAYBOOK.md.',
      },
    ],
    cta: {
      title: 'Need help rolling this out?',
      body: 'Book a demo walkthrough of review workflow and reports for your tenant.',
      button: 'Book a demo',
    },
  },
};

export const guidesCopy = { sr: srGuides, en: enGuides };

export const guideSlugs = Object.keys(srGuides) as GuideSlug[];

export function getGuide(slug: string, language: string): GuidePage | null {
  if (!guideSlugs.includes(slug as GuideSlug)) return null;
  return guidesCopy[language === 'en' ? 'en' : 'sr'][slug as GuideSlug];
}
